import type { TypedDocumentNode } from '@apollo/client'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { createUpload } from '@mux/upchunk'
import {
  AspectRatio,
  Button,
  Card,
  CardContent,
  CardFooter,
  Image,
  Progress,
  ProgressValue,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { FeatureFlag } from '@repo/shared-common'
import { useNavigate } from '@tanstack/react-router'
import DismissHelpPopover from '@web/src/components/primatives/dismiss-help-popover/dismiss-help-popover'
import LogoSymbol from '@web/src/components/primatives/logo-symbol/logo-symbol'
import { useTranslation } from '@web/src/lib/intl/react'
import type { ImTool } from 'imtool/lib/ImTool'
import { CheckCircleIcon, CircleXIcon, FilmIcon, ImagePlusIcon, PencilIcon, XIcon } from 'lucide-react'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'
import type {
  UploadCompletedMutation,
  UploadCompletedMutationVariables,
  UploadPresignedPostMutation,
  UploadPresignedPostMutationVariables,
} from 'types/graphql'
import { useAuth } from '../../../hooks/use-auth'
import ImageEditorDialog from '../image-editor-dialog/image-editor-dialog'

const PREVIEW_SIZE_PX = 300
const IMAGE_EDITOR_HELP_TYPE = 'imageEditor'

type UploadFile = File & { preview: string }

const getFromImage = async () => {
  if (typeof window === 'undefined') {
    throw new Error('imtool can only be used on the client side')
  }
  const { fromImage } = await import('imtool')
  return fromImage
}

// Picker and drag-and-drop files can point at a temporary handle. The first read
// (the thumbnail) succeeds, then a later read from the editor fails while the
// already-decoded thumbnail stays on screen. Copy the bytes into a normal File
// while the handle is still readable.
const cloneImageFile = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/')) {
    return file
  }

  try {
    const bytes = await file.arrayBuffer()
    return new File([bytes], file.name, { type: file.type, lastModified: file.lastModified })
  } catch (error) {
    console.error('Failed to read image file:', error)
    return file
  }
}

export const UPLOAD_PRESIGNED_POST: TypedDocumentNode<
  UploadPresignedPostMutation,
  UploadPresignedPostMutationVariables
> = gql`
  mutation UploadPresignedPost($input: UploadInput!) {
    uploadPresignedPost(input: $input) {
      mediaId
      uploadId
      storageDriver
      fileName
      uploadPath
      uploadUrl
      metadata
    }
  }
`

export const UPLOAD_COMPLETED: TypedDocumentNode<UploadCompletedMutation, UploadCompletedMutationVariables> = gql`
  mutation UploadCompleted($uploadId: String!) {
    uploadCompleted(uploadId: $uploadId)
  }
`

type UploadImagePreviewRef = {
  performUpload: ({
    albumId,
    placeId,
    publicationId,
    eventId,
    groupId,
    vendorMarketId,
    vendorMarketItemId,
  }: {
    albumId?: string
    placeId?: string
    publicationId?: string
    eventId?: string
    groupId?: string
    vendorMarketId?: string
    vendorMarketItemId?: string
  }) => void
  isUploadCompleted: () => boolean
} | null

interface UploadImagePreviewProps {
  className?: string
  imageIdx: number
  file: (File & { preview: string }) | null
  onDeleteImage?: (imageIdx: number) => void
  onEditImage?: (imageIdx: number) => void
  onUploadCompleted?: (imageIdx: number, mediaId?: string | null) => void
  onUploadFailed?: (imageIdx: number) => void
  isUploading?: boolean
  showEditHint?: boolean
}

export const UploadImagePreview = forwardRef<UploadImagePreviewRef, UploadImagePreviewProps>((props, forwardedRef) => {
  const { t } = useTranslation()
  const {
    imageIdx,
    file,
    onDeleteImage,
    onEditImage,
    onUploadCompleted,
    onUploadFailed,
    isUploading = false,
    showEditHint = false,
    className,
  } = props
  const [uploadProgress, setUploadProgress] = useState(0)
  const [tooltipError, setTooltipError] = useState('')

  const [uploadPresignedPost] = useMutation<UploadPresignedPostMutation, UploadPresignedPostMutationVariables>(
    UPLOAD_PRESIGNED_POST
  )
  const [uploadCompleted] = useMutation<UploadCompletedMutation, UploadCompletedMutationVariables>(UPLOAD_COMPLETED)

  useImperativeHandle(forwardedRef, () => ({
    isUploadCompleted: () => uploadProgress === 100,

    performUpload: async ({
      albumId,
      placeId,
      publicationId,
      eventId,
      groupId,
      vendorMarketId,
      vendorMarketItemId,
    }) => {
      setUploadProgress(1)

      let uploadFile: File | Blob = file
      let width: number | undefined
      let height: number | undefined
      let mimeType = file.type

      const cleanupProcessedBlob = () => {
        if (uploadFile !== file && uploadFile instanceof Blob) {
          uploadFile = null as unknown as File
        }
      }

      if (mimeType.startsWith('image/')) {
        let imImage: ImTool | null = null
        try {
          const fromImage = await getFromImage()
          imImage = await fromImage(uploadFile)
          const maxHeight = 1152
          const maxWidth = 2048

          if (imImage.height > maxHeight || imImage.width > maxWidth) {
            imImage.type('image/jpeg')
            imImage.quality(0.9)

            if (imImage.height > maxHeight) {
              const scale = maxHeight / imImage.height
              imImage.scale(imImage.width * scale, imImage.height * scale)
            } else if (imImage.width > maxWidth) {
              const scale = maxWidth / imImage.width
              imImage.scale(imImage.width * scale, imImage.height * scale)
            }

            setUploadProgress(4)
            uploadFile = await imImage.toBlob()
            mimeType = 'image/jpeg'
            setUploadProgress(5)
          }

          width = imImage.width
          height = imImage.height
        } catch (error) {
          console.error('Image processing error:', error)
          toast.error(t('Failed to process image: {{message}}', { message: (error as Error).message }))
          setTooltipError(t('Failed to process image'))
          setUploadProgress(-1)
          onUploadFailed?.(imageIdx)
          cleanupProcessedBlob()
          throw error
        } finally {
          if (imImage) {
            imImage = null
          }
        }
      }

      let result: { data: UploadPresignedPostMutation } | null = null
      try {
        result = await uploadPresignedPost({
          variables: {
            input: {
              mimeType: mimeType,
              fileSize: uploadFile.size,
              width: width,
              height: height,
              albumId: albumId,
              placeId: placeId,
              publicationId: publicationId,
              eventId: eventId,
              groupId: groupId,
              vendorMarketId: vendorMarketId,
              vendorMarketItemId: vendorMarketItemId,
            },
          },
        })
      } catch (error) {
        toast.error((error as Error).message)
        setTooltipError(t('Failed to upload image'))
        setUploadProgress(-1)
        onUploadFailed?.(imageIdx)
        cleanupProcessedBlob()
        throw error
      }

      const uploadDetails = result.data.uploadPresignedPost

      return new Promise<void>((resolve, reject) => {
        if (uploadDetails.storageDriver === 'S3') {
          const request = new XMLHttpRequest()
          const formData = new FormData()

          Object.entries(uploadDetails.metadata).forEach(([key, value]) => {
            formData.append(key, value as string)
          })

          formData.append('file', uploadFile, uploadDetails.fileName)
          request.open('POST', uploadDetails.uploadUrl)

          request.upload.onprogress = (e) => {
            setUploadProgress(Math.round((e.loaded / e.total) * 100))
          }

          request.onload = async () => {
            if (request.status >= 200 && request.status < 300) {
              try {
                await uploadCompleted({ variables: { uploadId: uploadDetails.uploadId } })
                onUploadCompleted?.(imageIdx, uploadDetails.mediaId)
                cleanupProcessedBlob()
                resolve()
              } catch (e) {
                onUploadFailed?.(imageIdx)
                toast.error((e as Error).message)
                cleanupProcessedBlob()
                reject(e)
              }
            } else {
              if (request.responseText.match('EntityTooLarge')) {
                toast.error(t('Image file is too large'))
                setTooltipError(t('Image file is too large'))
              } else {
                console.error(request)
              }
              setUploadProgress(-1)
              onUploadFailed?.(imageIdx)
              cleanupProcessedBlob()
              reject(new Error(`Upload failed with status ${request.status}`))
            }
          }

          request.onerror = () => {
            setUploadProgress(-1)
            onUploadFailed?.(imageIdx)
            cleanupProcessedBlob()
            reject(new Error('Network error during upload'))
          }

          request.send(formData)
        } else if (uploadDetails.storageDriver === 'mux') {
          const upload = createUpload({
            endpoint: uploadDetails.uploadUrl,
            file: file,
            chunkSize: 30_720,
          })

          upload.on('error', (error) => {
            if (onUploadFailed) {
              onUploadFailed(imageIdx)
            }
            toast.error(error.detail)
            cleanupProcessedBlob()
            reject(error)
          })

          upload.on('progress', (progress) => {
            setUploadProgress(progress.detail)
          })

          upload.on('success', async () => {
            try {
              await uploadCompleted({ variables: { uploadId: uploadDetails.uploadId } })
              if (onUploadCompleted) {
                onUploadCompleted(imageIdx, uploadDetails.mediaId)
              }
              cleanupProcessedBlob()
              resolve()
            } catch (e) {
              if (onUploadFailed) {
                onUploadFailed(imageIdx)
              }
              toast.error((e as Error).message)
              cleanupProcessedBlob()
              reject(e)
            }
          })
        }
      })
    },
  }))

  if (!file) return null

  const canEditOrRemove = (uploadProgress === 0 || uploadProgress < 0) && !isUploading
  const showEditFooter = canEditOrRemove && file.type.startsWith('image')

  return (
    <Card className={cn('relative w-full min-w-0 gap-0 overflow-hidden rounded-xl py-0', className)} key={file.preview}>
      <CardContent className="relative p-0">
        <AspectRatio className="w-full" ratio={1}>
          {file.type.startsWith('image') ? (
            <Image className="h-full w-full object-cover" src={file.preview} />
          ) : file.type.startsWith('video') ? (
            <video className="h-full w-full object-cover" controls>
              <source src={file.preview} type="video/mp4" />
              <track kind="captions" />
            </video>
          ) : (
            <div />
          )}
        </AspectRatio>

        {(uploadProgress !== 0 || isUploading) && (
          <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center bg-black/80">
            {((uploadProgress > 0 && uploadProgress < 100) || (isUploading && uploadProgress === 0)) && (
              <div className="absolute top-1/2 left-1/2 w-[85%] -translate-x-1/2 -translate-y-1/2">
                <Progress className="text-white" value={uploadProgress}>
                  <ProgressValue className="text-white" />
                </Progress>
              </div>
            )}
            {uploadProgress === 100 && <CheckCircleIcon className="text-[rgb(0,200,0)]" size={42} strokeWidth={2.75} />}
            {uploadProgress < 0 && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button className="inline-flex cursor-default" type="button">
                      <CircleXIcon className="text-[rgb(200,0,0)]" size={42} strokeWidth={2.75} />
                    </button>
                  }
                />
                <TooltipContent>{tooltipError || t('Upload failed')}</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}

        {canEditOrRemove && (
          <Button
            aria-label={t('Remove file')}
            className="absolute top-1 right-1 z-10 rounded-full ring-2 ring-gray-200"
            onClick={() => onDeleteImage?.(imageIdx)}
            size="icon-xs"
            type="button"
            variant="muted"
          >
            <XIcon className="size-4" />
          </Button>
        )}
      </CardContent>
      {showEditFooter ? (
        <CardFooter className="p-1">
          <DismissHelpPopover
            closeButtonClassName="text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground"
            content={t('You can edit your photo here before uploading.')}
            contentClassName="w-56 bg-secondary p-3 text-primary-foreground ring-secondary"
            descriptionClassName="text-secondary-foreground"
            helpType={IMAGE_EDITOR_HELP_TYPE}
            positionerClassName="[&_.Arrow_path]:fill-secondary"
            showPopover={showEditHint}
            side="top"
            sideOffset={8}
            wrapperProps={{ className: 'w-full' }}
          >
            <Button
              aria-label={t('Edit image')}
              className="w-full"
              onClick={() => onEditImage?.(imageIdx)}
              size="xs"
              type="button"
              variant="ghost"
            >
              <PencilIcon className="size-3.5" />
              {t('Edit')}
            </Button>
          </DismissHelpPopover>
        </CardFooter>
      ) : null}
    </Card>
  )
})

type MediaUploaderRef = {
  selectedMedia: number
  performUpload: ({
    albumId,
    placeId,
    publicationId,
    eventId,
    groupId,
    vendorMarketId,
    vendorMarketItemId,
  }: {
    albumId?: string
    placeId?: string
    publicationId?: string
    eventId?: string
    groupId?: string
    vendorMarketId?: string
    vendorMarketItemId?: string
  }) => void
} | null

interface MediaUploaderProps {
  className?: string
  disableVideo?: boolean
  maxFiles?: number
  onMediaSelected?: (count: number) => void
  onUploadsCompleted?: (uploadedMediaIds: string[]) => void
  onUploadFailure?: () => void
  /** Opens a popover next to the first preview's Edit button pointing out the image editor. */
  showEditHint?: boolean
}

const MediaUploader = forwardRef<MediaUploaderRef, MediaUploaderProps>((props, forwardedRef) => {
  const { t } = useTranslation()
  const {
    maxFiles,
    disableVideo = false,
    onMediaSelected,
    onUploadsCompleted,
    onUploadFailure,
    showEditHint = false,
    className,
    ...rest
  } = props
  const imagePreviews = useRef([])
  const uploadedMediaIds = useRef<string[]>([])
  const [files, setFiles] = useState<UploadFile[]>([])
  const previousFilesRef = useRef<UploadFile[]>([])
  const [disabled, setDisabled] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const { hasPermission, getFeature } = useAuth()
  const navigate = useNavigate()

  const featureMaxFiles = getFeature(FeatureFlag.MEDIA_UPLOAD_LIMIT)
  const effectiveMaxFiles =
    maxFiles && featureMaxFiles ? Math.min(maxFiles, featureMaxFiles) : maxFiles || featureMaxFiles
  const supportedMimeTypes: Record<string, string[]> = {
    'image/jpeg': [],
    'image/png': [],
    'image/svg': [],
    'image/bmp': [],
    'image/gif': [],
    'image/ico': [],
    'image/tiff': [],
    'image/avif': [],
    'image/webp': [],
  }

  if (hasPermission('media:upload-video') && !disableVideo) {
    supportedMimeTypes['video/3gpp'] = []
    supportedMimeTypes['video/3gpp2'] = []
    supportedMimeTypes['video/avi'] = []
    supportedMimeTypes['video/m4v'] = []
    supportedMimeTypes['video/mov'] = []
    supportedMimeTypes['video/mp4'] = []
    supportedMimeTypes['video/mpeg'] = []
    supportedMimeTypes['video/mpg'] = []
    supportedMimeTypes['video/ogg'] = []
    supportedMimeTypes['video/ogv'] = []
    supportedMimeTypes['video/quicktime'] = []
    supportedMimeTypes['video/webm'] = []
    supportedMimeTypes['video/wmv'] = []
    supportedMimeTypes['video/x-flv'] = []
    supportedMimeTypes['video/x-matroska'] = []
    supportedMimeTypes['video/x-ms-asf'] = []
    supportedMimeTypes['video/x-ms-mpeg'] = []
    supportedMimeTypes['video/x-ms-wm'] = []
    supportedMimeTypes['video/x-ms-wmv'] = []
    supportedMimeTypes['video/x-ms-wmx'] = []
    supportedMimeTypes['video/x-ms-wvx'] = []
    supportedMimeTypes['video/x-msvideo'] = []
  }

  useEffect(() => {
    if (onMediaSelected) {
      onMediaSelected(files.length)
    }
  }, [files, onMediaSelected])

  const createPreviewUrl = async (file: File): Promise<string> => {
    if (file.type.startsWith('image/')) {
      let imImage: ImTool | null = null
      try {
        const fromImage = await getFromImage()
        imImage = await fromImage(file)
        const maxWidth = PREVIEW_SIZE_PX
        const maxHeight = PREVIEW_SIZE_PX

        if (imImage.width > maxWidth || imImage.height > maxHeight) {
          const scaleX = maxWidth / imImage.width
          const scaleY = maxHeight / imImage.height
          const scale = Math.min(scaleX, scaleY)

          imImage.scale(imImage.width * scale, imImage.height * scale)
        }

        const previewBlob = await imImage.toBlob()
        const url = URL.createObjectURL(previewBlob)
        return url
      } catch (error) {
        console.error('Failed to create preview for image:', error)
        return URL.createObjectURL(file)
      } finally {
        if (imImage) {
          imImage = null
        }
      }
    } else {
      return URL.createObjectURL(file)
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      ...supportedMimeTypes,
    },
    multiple: effectiveMaxFiles !== 1,
    onDropAccepted: async (acceptedFiles) => {
      const curLen = files.length

      setIsProcessing(true)

      try {
        const stableFiles: File[] = []
        for (let idx = 0; idx < acceptedFiles.length; idx++) {
          if (effectiveMaxFiles && curLen + idx >= effectiveMaxFiles) {
            break
          }
          // Read every file before building thumbnails. Thumbnail decoding is slow
          // enough that a later picker handle can expire while an earlier image is processed.
          stableFiles.push(await cloneImageFile(acceptedFiles[idx]))
        }

        for (const file of stableFiles) {
          const previewUrl = await createPreviewUrl(file)
          const fileWithPreview = Object.assign(file, {
            preview: previewUrl,
          })

          setFiles((prevFiles) => [...prevFiles, fileWithPreview])
        }
      } finally {
        setIsProcessing(false)
      }
    },
  })

  useImperativeHandle(forwardedRef, () => ({
    selectedMedia: files.length,

    performUpload: async ({
      albumId,
      placeId,
      publicationId,
      eventId,
      groupId,
      vendorMarketId,
      vendorMarketItemId,
    }) => {
      setDisabled(true)
      setIsUploading(true)
      uploadedMediaIds.current = []
      if (imagePreviews.current.length > 0) {
        for (let i = 0; i < imagePreviews.current.length; i++) {
          const child = imagePreviews.current[i]
          if (child) {
            try {
              await child.performUpload({
                albumId,
                placeId,
                publicationId,
                eventId,
                groupId,
                vendorMarketId,
                vendorMarketItemId,
              })
            } catch (error) {
              console.error(`Upload failed for image ${i}:`, error)
            }
          }
        }
        setDisabled(false)
        setIsUploading(false)
        setFiles([])
        if (onUploadsCompleted) {
          onUploadsCompleted(uploadedMediaIds.current)
        }
      } else {
        setDisabled(false)
        setIsUploading(false)
        setFiles([])
        if (onUploadsCompleted) {
          onUploadsCompleted([])
        }
      }
    },
  }))

  useEffect(() => {
    const previousFiles = previousFilesRef.current
    const nextPreviews = new Set(files.map((file) => file.preview))
    for (const file of previousFiles) {
      if (file.preview && !nextPreviews.has(file.preview)) {
        URL.revokeObjectURL(file.preview)
      }
    }
    previousFilesRef.current = files
  }, [files])

  useEffect(() => {
    return () => {
      for (const file of previousFilesRef.current) {
        if (file.preview) {
          URL.revokeObjectURL(file.preview)
        }
      }
    }
  }, [])

  const handleRemoveImage = (index) => {
    setEditingIndex((current) => {
      if (current === null) {
        return null
      }
      if (current === index) {
        return null
      }
      if (current > index) {
        return current - 1
      }
      return current
    })
    setFiles((prevFiles) => {
      if (prevFiles[index]?.preview) {
        URL.revokeObjectURL(prevFiles[index].preview)
      }
      return prevFiles.filter((_file, i) => i !== index)
    })
    imagePreviews.current = imagePreviews.current.filter((_, i) => i !== index)
  }

  const handleApplyEditedImage = async (editedFile: File) => {
    if (editingIndex === null) {
      return
    }

    const index = editingIndex
    const previewUrl = await createPreviewUrl(editedFile)

    setFiles((prevFiles) => {
      const nextFiles = [...prevFiles]
      if (nextFiles[index]?.preview) {
        URL.revokeObjectURL(nextFiles[index].preview)
      }
      nextFiles[index] = Object.assign(editedFile, { preview: previewUrl })
      return nextFiles
    })
    setEditingIndex(null)
  }

  const addPhotosLabel = hasPermission('media:upload-video')
    ? disableVideo
      ? t('Add Photos')
      : t('Add Photos/Videos')
    : t('Add Photos')

  return (
    <>
      <Card className={cn('w-full gap-0 p-0', className)} {...rest}>
        <CardContent className="flex min-h-40 flex-col items-center justify-center p-2">
          <div className="flex h-full w-full flex-grow flex-col items-center justify-center">
            <input {...getInputProps()} disabled={disabled || isProcessing} />
            <div
              {...getRootProps({ className: 'dropzone' })}
              className={cn(
                'flex h-full w-full flex-grow flex-col items-center justify-center rounded-md border-2 border-muted-foreground/50 border-dashed transition-colors',
                isProcessing ? 'cursor-wait' : 'cursor-pointer',
                (effectiveMaxFiles && files.length >= effectiveMaxFiles) || disabled ? 'hidden' : 'flex',
                !(isProcessing || disabled) && 'hover:border-primary'
              )}
            >
              {isProcessing ? (
                <div className="flex flex-col items-center gap-2 py-2">
                  <Spinner className="size-6" />
                  <p className="font-medium text-base">{t('Scaling Media Please Wait...')}</p>
                </div>
              ) : (
                <div className="flex w-full items-center justify-evenly py-2">
                  <div className="flex flex-col items-center gap-0">
                    <ImagePlusIcon aria-hidden className="my-2 size-8" />
                    <p className="font-medium text-base">{addPhotosLabel}</p>
                    <p className="text-muted-foreground text-sm">{t('or drag and drop')}</p>
                  </div>
                </div>
              )}
            </div>

            {files.length > 0 && (
              <div className="grid w-full grid-cols-2 gap-2 pt-2">
                {files.map((file, idx) => {
                  const isCenteredLonePreview = files.length % 2 === 1 && idx === files.length - 1
                  return (
                    <div className={cn(isCenteredLonePreview && 'col-span-2 flex justify-center')} key={file.preview}>
                      <div className={cn('min-w-0', isCenteredLonePreview ? 'w-[calc((100%-0.5rem)/2)]' : 'w-full')}>
                        <UploadImagePreview
                          file={file}
                          imageIdx={idx}
                          isUploading={isUploading}
                          onDeleteImage={(imageIdx) => handleRemoveImage(imageIdx)}
                          onEditImage={(imageIdx) => setEditingIndex(imageIdx)}
                          onUploadCompleted={(_imageIdx, mediaId) => {
                            if (mediaId) {
                              uploadedMediaIds.current.push(mediaId)
                            }
                          }}
                          onUploadFailed={() => {
                            if (onUploadFailure) {
                              onUploadFailure()
                            }
                          }}
                          ref={(el) => {
                            imagePreviews.current[idx] = el
                          }}
                          showEditHint={showEditHint && idx === 0}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </CardContent>
        {!(hasPermission('media:upload-video') || disableVideo) && (
          <CardFooter className="flex flex-col gap-0 border-gray-200 border-t p-2">
            <div className="flex w-full items-center justify-center gap-2">
              <FilmIcon aria-hidden className="size-8" />
              <p className="font-medium text-base">{t('To Upload Videos')}</p>
              <FilmIcon aria-hidden className="size-8" />
            </div>

            <Button
              className="w-full"
              onClick={(e) => {
                e.stopPropagation()
                navigate({ to: '/supporter' })
              }}
              type="button"
              variant="ghost"
            >
              <div className="flex w-full items-center justify-center gap-1">
                <p className="font-medium text-base">{t('Please Support')}</p>
                <LogoSymbol imageClassName="w-6" />
              </div>
            </Button>
          </CardFooter>
        )}
      </Card>
      <ImageEditorDialog
        file={editingIndex !== null ? files[editingIndex] : null}
        onApply={handleApplyEditedImage}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setEditingIndex(null)
          }
        }}
        open={editingIndex !== null}
      />
    </>
  )
})

export default MediaUploader
